import {
  enforceCallsRuntimeCertification,
  type CallsProtectedRuntimeOperation,
  type CallsRuntimeCertificationEnforcementResult,
} from './CallsRuntimeCertificationEnforcementGate';

export type CallsRuntimeHandlerEnforcementMode =
  | 'observe'
  | 'enforce';

export type CallsRuntimeHandlerOwner =
  | 'Messages.startCall'
  | 'NativeCallPage.startCall'
  | 'Messages.acceptIncomingCall'
  | 'Messages.declineIncomingCall'
  | 'CallModal.connectMedia'
  | 'CallModal.endCall'
  | 'NativeCallPage.endCall'
  | 'KinsCircleMessagingService.onMessageReceived'
  | 'IncomingCallService.startForeground';

export interface CallsRuntimeHandlerRegistration {
  readonly owner: CallsRuntimeHandlerOwner;
  readonly operation: CallsProtectedRuntimeOperation;
  readonly platform: 'web' | 'android-native';
  readonly responsibility:
    | 'outgoing-start'
    | 'incoming-delivery'
    | 'incoming-answer'
    | 'incoming-decline'
    | 'media-connection'
    | 'call-release';
}

export interface CallsRuntimeHandlerCertificationResult {
  readonly bridge: 'CallsRuntimeHandlerCertificationBridge';
  readonly owner: CallsRuntimeHandlerOwner;
  readonly operation: CallsProtectedRuntimeOperation;
  readonly mode: CallsRuntimeHandlerEnforcementMode;
  readonly certificationDecision:
    CallsRuntimeCertificationEnforcementResult['decision'];
  readonly certificationAllowed: boolean;
  readonly runtimeAllowed: boolean;
  readonly reasonCode: string;
  readonly reason: string;
  readonly evaluatedAt: string;
}

export const CALLS_RUNTIME_HANDLER_REGISTRY:
  readonly CallsRuntimeHandlerRegistration[] = Object.freeze([
    Object.freeze({
      owner: 'Messages.startCall',
      operation: 'start-outgoing-call',
      platform: 'web',
      responsibility: 'outgoing-start',
    }),
    Object.freeze({
      owner: 'NativeCallPage.startCall',
      operation: 'start-outgoing-call',
      platform: 'web',
      responsibility: 'outgoing-start',
    }),
    Object.freeze({
      owner: 'KinsCircleMessagingService.onMessageReceived',
      operation: 'deliver-incoming-call',
      platform: 'android-native',
      responsibility: 'incoming-delivery',
    }),
    Object.freeze({
      owner: 'IncomingCallService.startForeground',
      operation: 'deliver-incoming-call',
      platform: 'android-native',
      responsibility: 'incoming-delivery',
    }),
    Object.freeze({
      owner: 'Messages.acceptIncomingCall',
      operation: 'answer-incoming-call',
      platform: 'web',
      responsibility: 'incoming-answer',
    }),
    Object.freeze({
      owner: 'Messages.declineIncomingCall',
      operation: 'release-call',
      platform: 'web',
      responsibility: 'incoming-decline',
    }),
    Object.freeze({
      owner: 'CallModal.connectMedia',
      operation: 'connect-call-media',
      platform: 'web',
      responsibility: 'media-connection',
    }),
    Object.freeze({
      owner: 'CallModal.endCall',
      operation: 'release-call',
      platform: 'web',
      responsibility: 'call-release',
    }),
    Object.freeze({
      owner: 'NativeCallPage.endCall',
      operation: 'release-call',
      platform: 'web',
      responsibility: 'call-release',
    }),
  ]);

export function getCallsRuntimeHandlerRegistration(
  owner: CallsRuntimeHandlerOwner,
): CallsRuntimeHandlerRegistration | null {
  return (
    CALLS_RUNTIME_HANDLER_REGISTRY.find(
      (registration) => registration.owner === owner,
    ) ?? null
  );
}

/**
 * Evaluates the CG-024 decision for a verified Calls runtime owner.
 *
 * Observe mode is intentionally non-blocking so KinsCircle can perform the
 * real-device tests required to earn certification. Enforce mode fails closed.
 */
export function evaluateCallsRuntimeHandlerCertification(
  owner: CallsRuntimeHandlerOwner,
  mode: CallsRuntimeHandlerEnforcementMode = 'observe',
): CallsRuntimeHandlerCertificationResult {
  const registration = getCallsRuntimeHandlerRegistration(owner);

  if (!registration) {
    return Object.freeze({
      bridge: 'CallsRuntimeHandlerCertificationBridge',
      owner,
      operation: 'release-call',
      mode,
      certificationDecision: 'block',
      certificationAllowed: false,
      runtimeAllowed: false,
      reasonCode: 'CALLS_RUNTIME_HANDLER_UNREGISTERED',
      reason: `Calls runtime owner "${owner}" is not registered.`,
      evaluatedAt: new Date().toISOString(),
    });
  }

  const enforcement = enforceCallsRuntimeCertification(
    registration.operation,
  );

  const runtimeAllowed =
    mode === 'observe' ? true : enforcement.allowed;

  return Object.freeze({
    bridge: 'CallsRuntimeHandlerCertificationBridge',
    owner,
    operation: registration.operation,
    mode,
    certificationDecision: enforcement.decision,
    certificationAllowed: enforcement.allowed,
    runtimeAllowed,
    reasonCode:
      mode === 'observe' && !enforcement.allowed
        ? 'CALLS_RUNTIME_OBSERVATION_ALLOWED'
        : enforcement.reasonCode,
    reason:
      mode === 'observe' && !enforcement.allowed
        ? `Runtime observation is allowed so live-device certification evidence can be collected. CG-024 decision: ${enforcement.reason}`
        : enforcement.reason,
    evaluatedAt: enforcement.evaluatedAt,
  });
}
